import { assetRecordMutationId, assetRecordPrimaryId } from './assetRecordPrimaryId';
import { deriveEightDigitIdentifierFromUuid } from './businessClientRegistrationIdentifier';

/** Client-facing 8-digit id shown on asset detail (not an API UUID). */
export function isAssetShortDisplayId(value: string): boolean {
  return /^\d{8}$/.test(value.trim());
}

/** Deterministic 8-digit display id from a UUID (same algorithm as business client ids). */
export function formatAssetShortId(id: string): string {
  const derived = deriveEightDigitIdentifierFromUuid(id);
  if (derived) {
    return derived;
  }
  const trimmed = id.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.length > 8 ? trimmed.slice(0, 8) : trimmed;
}

/** Short numeric id for an asset record, or null when no primary id exists. */
export function assetRecordShortDisplayId(data: Record<string, unknown>): string | null {
  const id = assetRecordPrimaryId(data);
  if (!id) {
    return null;
  }
  const short = formatAssetShortId(id);
  return short || null;
}

function recordIdsForShortDisplay(data: Record<string, unknown>): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (raw: string | null) => {
    const trimmed = raw?.trim() ?? '';
    if (!trimmed) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    ids.push(trimmed);
  };
  add(assetRecordPrimaryId(data));
  add(assetRecordMutationId(data));
  return ids;
}

/** True when `displayId` is the record UUID/API id or its 8-digit client-facing id. */
export function assetRecordMatchesDisplayId(
  data: Record<string, unknown>,
  displayId: string,
): boolean {
  const want = displayId.trim();
  if (!want) {
    return false;
  }
  const wantKey = want.toLowerCase();
  const ids = recordIdsForShortDisplay(data);
  for (const id of ids) {
    if (id.toLowerCase() === wantKey) {
      return true;
    }
  }
  if (!isAssetShortDisplayId(want)) {
    return false;
  }
  return ids.some((id) => formatAssetShortId(id) === want);
}
